import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { ERROR_SNACKBAR, SnackbarInfoType, SUCCESS_SNACKBAR } from '@nagel-types';
import { filter, interval, merge, take } from 'rxjs';
import { SnackbarComponent } from './snackbar/snackbar.component';

const DEFAULT_SNACKBAR_INFO: SnackbarInfoType = {
    message: '',
    action: '',
    type: SUCCESS_SNACKBAR
};

const SNACKBAR_POSITION = 'top';

const SHORT_SNACKBAR_DURATION = 4000;
const LONG_SNACKBAR_DURATION = 7000;
const MESSAGE_SMALL_LENGTH = 15;
const UNLIMITED_SNACKBAR_DURATION = 0;

const CHECK_INTERVAL = 800;

@Injectable({
    providedIn: 'root'
})
export class SnackbarService {
    private notificationsQueue: SnackbarInfoType[] = [];
    private snackBarRef!: MatSnackBarRef<unknown>;
    private hasSnackbarOpen = false;

    constructor(
      private _matSnackBar: MatSnackBar,
      private _ngZone: NgZone
    ) {
        this._ngZone.runOutsideAngular(() => {
            interval(CHECK_INTERVAL).pipe(
                filter(() => this.notificationsQueue.length > 0 && this.hasSnackbarOpen === false)
            ).subscribe(() => this.showSnackbar());
        });
    }

    public pushSnackbar(snackbarInfo: SnackbarInfoType) {
        snackbarInfo.type === ERROR_SNACKBAR
            ? this.notificationsQueue.unshift(snackbarInfo)
            : this.notificationsQueue.push(snackbarInfo);
    }

    //Keeps the given order instead of moving errors to the front of the queue
    public pushSnackbarSequence(snackbarInfos: SnackbarInfoType[]) {
        this.notificationsQueue.push(...snackbarInfos);
    }

    private showSnackbar() {
        const snackbarInfo: SnackbarInfoType = this.notificationsQueue.shift() || DEFAULT_SNACKBAR_INFO;
        const { message, action, type, persistent } = snackbarInfo;

        this.snackBarRef = this._matSnackBar.openFromComponent(SnackbarComponent, {
            data: { message, action, type, persistent },
            verticalPosition: SNACKBAR_POSITION,
            panelClass: 'snackbar',
            duration: this.getSnackbarDuration(message, persistent)
        });

        this.hasSnackbarOpen = true;

        merge(
            this.snackBarRef.afterDismissed(),
            this.snackBarRef.onAction()
        ).pipe(take(1)).subscribe(() => {
            this.hasSnackbarOpen = false;
        });
    }

    private getSnackbarDuration(message: string, persistent?: boolean): number {
        let result = SHORT_SNACKBAR_DURATION;

        if (persistent) {
            result = UNLIMITED_SNACKBAR_DURATION;
        } else if (message.length > MESSAGE_SMALL_LENGTH) {
            result = LONG_SNACKBAR_DURATION;
        }
        return result;
    }

    public dismissSnackbar() {
        if (this.snackBarRef && this.hasSnackbarOpen) {
            this.snackBarRef.dismiss();
            this.hasSnackbarOpen = false;
        }
    }
}